import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import toast from "react-hot-toast";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { RiCloseLargeFill } from "react-icons/ri";
import ImageTemplateServices from "../../../services/ImageTemplateServices";
import BoardTabs, { yantrasForTab } from "./BoardTabs";

const api = new ImageTemplateServices();

const fileBase = () => `${process.env.REACT_APP_NODE_URL || ""}/api/yantra`;

const AddImageTemplate = ({ show, onHide, yantras, games = [], onSaved }) => {
  const previewUrlsRef = useRef({});
  const [tab, setTab] = useState("all");

  const safeYantras = useMemo(
    () => (Array.isArray(yantras) ? yantras : []),
    [yantras],
  );

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        templateName: Yup.string()
          .trim()
          .required("Template name is required")
          .max(255, "Max 255 characters"),
        isActive: Yup.boolean(),
        // Names and images are per-yantra overrides: any yantra left alone
        // simply keeps its default. A template with nothing in it is the
        // only thing refused.
        yantraNames: Yup.object(),
        files: Yup.object().test(
          "at-least-one-image",
          "Upload at least one yantra image",
          (value) =>
            !!value && Object.values(value).some((v) => v instanceof File),
        ),
      }),
    [safeYantras],
  );

  const initialValues = useMemo(
    () => ({
      templateName: "",
      isActive: true,
      yantraNames: Object.fromEntries(safeYantras.map((y) => [y.id, y.name || ""])),
      files: {},
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [safeYantras],
  );

  const revokeAll = () => {
    Object.values(previewUrlsRef.current).forEach((u) => {
      if (u && String(u).startsWith("blob:")) {
        try {
          URL.revokeObjectURL(u);
        } catch (_) {}
      }
    });
    previewUrlsRef.current = {};
  };

  useEffect(() => {
    if (!show) {
      revokeAll();
      setTab("all");
    }
  }, [show]);

  useEffect(() => () => revokeAll(), []);

  const setPreview = (yantraId, file) => {
    const old = previewUrlsRef.current[yantraId];
    if (old && String(old).startsWith("blob:")) URL.revokeObjectURL(old);
    if (file) {
      previewUrlsRef.current[yantraId] = URL.createObjectURL(file);
    } else {
      delete previewUrlsRef.current[yantraId];
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      scrollable
      backdrop="static"
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnMount={false}
        enableReinitialize
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          try {
            const fd = new FormData();
            fd.append("template_name", values.templateName.trim());
            fd.append("is_active", values.isActive ? "1" : "0");
            Object.entries(values.yantraNames || {}).forEach(([yantraId, name]) => {
              fd.append(`yantra_name_${yantraId}`, String(name || "").trim());
            });
            Object.entries(values.files).forEach(([yantraId, file]) => {
              if (file instanceof File) fd.append(`yantra_${yantraId}`, file);
            });
            const res = await api.createTemplate(fd);
            if (res.isSuccess) {
              toast.success(res.message || "Template created");
              onSaved?.();
              resetForm();
              revokeAll();
              onHide();
            } else {
              toast.error(res.message || "Create failed");
            }
          } catch (err) {
            toast.error(
              err?.response?.data?.message || err?.message || "Create failed",
            );
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          isSubmitting,
          setFieldValue,
          setFieldTouched,
          submitCount,
        }) => {
          const handleYantraName = (yantraId, val) => {
            setFieldValue("yantraNames", { ...values.yantraNames, [yantraId]: val });
            setFieldTouched("yantraNames", true, false);
          };
          const handleFile = (yantraId, file) => {
            if (!file) return;
            setPreview(yantraId, file);
            setFieldValue("files", { ...values.files, [yantraId]: file });
            setFieldTouched("files", true, false);
          };
          const clearYantra = (yantraId) => {
            setPreview(yantraId, null);
            const next = { ...values.files };
            delete next[yantraId];
            setFieldValue("files", next);
            setFieldTouched("files", true, false);
          };
          const filesError =
            (touched.files || submitCount > 0) && errors.files;
          return (
            <Form>
              <div className="it-modal-head">
                <h5 className="it-modal-title">Add image template</h5>
                <button
                  type="button"
                  className="it-icon-close"
                  onClick={onHide}
                  aria-label="Close"
                >
                  <RiCloseLargeFill size={20} />
                </button>
              </div>
              <Modal.Body className="it-modal-body">
                <div className="it-field">
                  <label htmlFor="templateName" className="it-label">
                    Template name{" "}
                    <span className="required-asterisk">*</span>
                  </label>
                  <Field
                    id="templateName"
                    name="templateName"
                    placeholder="e.g. template name"
                    maxLength={255}
                    className={`it-input ${
                      errors.templateName && touched.templateName
                        ? "input-error"
                        : ""
                    }`}
                  />
                  <ErrorMessage name="templateName">
                    {(msg) => <div className="admin_error-text">{msg}</div>}
                  </ErrorMessage>
                </div>

                <div className="it-field it-row">
                  <label className="it-check">
                    <input
                      type="checkbox"
                      checked={values.isActive}
                      onChange={(e) =>
                        setFieldValue("isActive", e.target.checked)
                      }
                    />
                    <span>Active</span>
                  </label>
                </div>

                <p className="it-hint">
                  Upload an image for any yantra you want to look different
                  for players on this template. Yantras left alone keep the
                  default image. Switch tabs to see each game's board.
                </p>

                {filesError && (
                  <div className="admin_error-text it-files-error">
                    {filesError}
                  </div>
                )}

                <BoardTabs
                  games={games}
                  total={safeYantras.length}
                  covered={
                    new Set(
                      Object.entries(values.files)
                        .filter(([, v]) => v instanceof File)
                        .map(([id]) => Number(id)),
                    )
                  }
                  value={tab}
                  onChange={setTab}
                />

                <div className="it-yantra-grid">
                  {yantrasForTab(tab, safeYantras, games).map((y, idx) => {
                    const file = values.files[y.id];
                    const previewUrl = file
                      ? previewUrlsRef.current[y.id]
                      : null;
                    const defaultUrl = y.image
                      ? `${fileBase()}/${encodeURIComponent(y.image)}`
                      : null;
                    return (
                      <div
                        key={y.id}
                        className={`it-yantra-card${file ? " it-yantra-card-set" : ""}`}
                      >
                        <div className="it-yantra-head">
                          <input
                            type="text"
                            className="it-yantra-name-input"
                            placeholder={y.name || "Yantra name"}
                            maxLength={255}
                            value={values.yantraNames?.[y.id] ?? ""}
                            onChange={(e) => handleYantraName(y.id, e.target.value)}
                          />
                          <span className="it-yantra-id"># {idx + 1}</span>
                        </div>
                        {file && previewUrl ? (
                          <div className="it-preview-wrap">
                            <img
                              src={previewUrl}
                              alt=""
                              className="it-preview-img"
                            />
                            <div className="it-preview-actions">
                              <label className="it-btn-secondary it-file-label">
                                Re-upload
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="it-file-input"
                                  onChange={(e) =>
                                    handleFile(y.id, e.target.files?.[0])
                                  }
                                />
                              </label>
                              <button
                                type="button"
                                className="it-btn-danger"
                                onClick={() => clearYantra(y.id)}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ) : (
                          <label className="it-upload-zone it-upload-zone--default">
                            <input
                              type="file"
                              accept="image/*"
                              className="it-file-input"
                              onChange={(e) =>
                                handleFile(y.id, e.target.files?.[0])
                              }
                            />
                            {defaultUrl && (
                              <img src={defaultUrl} alt="" className="it-default-img" />
                            )}
                            <span className="it-default-tag">Default</span>
                            <span>Choose image</span>
                          </label>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Modal.Body>
              <Modal.Footer className="it-modal-footer">
                <button
                  type="button"
                  className="it-btn-ghost"
                  onClick={onHide}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="it-btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving…" : "Save template"}
                </button>
              </Modal.Footer>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default AddImageTemplate;
